import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ProjectGrid } from './ProjectGrid';
import { CollectionsView } from './CollectionsView';
import { FilesView } from './FilesView';
import { Search, Grid3X3, List, Sparkles, Settings, SearchIcon } from 'lucide-react';

export function MainContent({ 
  activeSection, 
  selectedProject,
  selectedCollection,
  currentView,
  onNewProject, 
  onSettingsClick,
  onProjectSelect,
  onCollectionSelect,
  onBackToProjects,
  onBackToCollections,
  onFileSelect,
  userInfo,
  onCollectionUpdated,  // New callback to notify when collections are updated
  collectionsCache,      // Cache of collection data with updated file counts
  shouldRefreshData     // Flag to trigger data refresh when coming back from Workspace
}) {
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState(""); // Separate input state
  const [sortBy, setSortBy] = useState("modified");
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300); // 300ms debounce delay
    
    return () => clearTimeout(timer);
  }, [searchInput]);

  const getHeading = () => {
    if (selectedCollection) return selectedCollection.name;
    if (selectedProject) return selectedProject.name;
    return activeSection;
  };

  const getSubheading = () => {
    if (selectedCollection) {
      return "Files & digital documents";
    }
    if (selectedProject) {
      return "Collections & digital assets";
    }
    return `${activeSection === "Recents" ? "Recently accessed" : "Complete collection of"} digitized documents`;
  };

  const handleCollectionClick = (collection) => {
    onCollectionSelect(collection);
  };

  const handleFileDeleted = (fileId, collectionId) => {
    // When a file is deleted, notify parent to update collection counts
    if (onCollectionUpdated && collectionId) {
      onCollectionUpdated(collectionId, 'fileDeleted');
    }
  };

  return (
    
    <div className="flex-1 flex flex-col min-h-0 bg-slate-950">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-[#2a282a] flex-shrink-0">
        {/* <div className="absolute inset-0 bg-[radial-gradient(circle_at_10%_20%,rgba(21,20,21,0.5),transparent_80%)]"/> */}
        {/* <div className="absolute inset-0" /> */}
        <div className="relative px-12 py-12">
          <div className="grid grid-cols-12 gap-8 items-start">
            {/* Main heading - asymmetrical layout */}
            <div className="col-span-8 min-w-0">
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl font-bold text-gray-100 leading-tight text-left tracking-tight">
                  {getHeading()}
                </h1>
                <div className="flex items-center gap-3">
                  <p className="text-lg text-gray-400 font-medium">
                    {getSubheading()}
                  </p>
                </div>
              </div>
            </div>

            {/* Action buttons - positioned right */}
            <div className="col-span-4 flex justify-end items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={onSettingsClick}
                className="h-10 w-10"
              >
                <Settings className="h-5 w-5 text-gray-400" />
              </Button>
              <Button 
                onClick={onNewProject}
                className="bg-[#494646] hover:bg-[#151415] text-white px-6 py-3 h-auto text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-200 group"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 group-hover:rotate-12 transition-transform duration-300" />
                  New Project
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Universal Search and Controls Bar */}
      <div className="px-12 py-6 bg-[#2a282a] backdrop-blur-sm border-b border-[#494646] flex-shrink-0">
        <div className="flex items-center justify-between gap-8">
          {/* Search - prominent space */}
          <div className="relative flex-1 max-w-2xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-50"/>
              <Input
                placeholder={
                  selectedCollection 
                    ? `Search files in ${selectedCollection.name}...`
                    : selectedProject 
                      ? `Search collections in ${selectedProject.name}...`
                      : "Search through your digital universe..."
                }
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-12 pr-4 py-4 h-12 bg-[#151415] backdrop-blur-sm hover:border-blue-400 focus:border-blue-400 text-base rounded-xl shadow-sm transition-all duration-300"
              />
            </div>
          </div>

          {/* Controls cluster */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3 px-4 py-2 bg-[#151415] rounded-lg">
              <span className="text-sm text-gray-300 font-medium">Sort</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-[#2a282a] border-0 text-sm text-gray-200 rounded-md px-2 py-1 shadow-sm cursor-pointer"
              >
                <option value="modified">Last Modified</option>
                <option value="created">Date Created</option>
                <option value="name">Name</option>
                {selectedCollection ? (
                  <option value="size">File Size</option>
                ) : selectedProject ? (
                  <option value="files">File Count</option>
                ) : (
                  <option value="collections">Collections Count</option>
                )}
              </select>
            </div>

            <div className="flex items-center bg-[#151415] rounded-xl p-1 shadow-sm">
              <Button
                variant={viewMode === "grid" ? "toggle_active" : "ghost"}
                size="sm"
                onClick={() => setViewMode("grid")}
                className={`rounded-lg text- h-8 px-3 transition-all duration-200 ${
                  viewMode === "grid"
                    ? "bg-[#2a282a] text-white shadow-sm"
                    : "hover:bg-[#494646] text-gray-400 hover:text-gray-200"
                }`}
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "list" ? "toggle_active" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                className={`rounded-lg h-8 px-3 transition-all duration-200 ${
                  viewMode === "list"
                    ? "bg-[#2a282a] text-white shadow-sm"
                    : "hover:bg-[#494646] text-gray-400 hover:text-gray-200"
                }`}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      

      {/* Content Area */}
      <div className="flex-1 px-12 py-8 bg-gradient-to-br from-[#2a282a] to-[#151415] overflow-auto">
        {currentView === "files" ? (
          <FilesView
            project={selectedProject}
            collection={selectedCollection}
            userInfo={userInfo}
            onBack={onBackToCollections}
            onFileClick={onFileSelect}
            searchQuery={searchQuery}
            sortBy={sortBy}
            viewMode={viewMode}
            onFileDeleted={handleFileDeleted}
            onCollectionUpdated={onCollectionUpdated}
            shouldRefreshData={shouldRefreshData}
          />
        ) : currentView === "collections" ? (
          <CollectionsView
            project={selectedProject}
            userInfo={userInfo}
            onBack={onBackToProjects}
            onCollectionClick={handleCollectionClick}
            onFileSelect={onFileSelect}
            searchQuery={searchQuery}
            sortBy={sortBy}
            viewMode={viewMode}
            collectionsCache={collectionsCache}
            onCollectionUpdated={onCollectionUpdated}
          />
        ) : (
          <ProjectGrid 
            searchQuery={searchQuery} 
            activeSection={activeSection} 
            selectedProject={selectedProject}
            viewMode={viewMode}
            userInfo={userInfo}
            onProjectClick={onProjectSelect}
          />
        )}
      </div>
    </div>
  )
}